import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Building2, FileText, Truck, Send } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";
import JunkFolderHint from "../components/JunkFolderHint";

const enquiryTypes = [
  "General",
  "Bulk / Lab Supply",
  "UK Dispatch",
  "COA Request",
  "Other",
];

export default function Contact() {
  usePageMeta("Contact — Peptigen Labs", "UK research enquiries, bulk lab supply, COA requests.");
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    enquiry_type: "General",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // email address we sent to (for success state)

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll get back to you shortly");
      setSent(form.email);
      setForm({ name: "", email: "", organisation: "", enquiry_type: "General", message: "" });
    } catch {
      toast.error("Could not send your message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="contact-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="pg-micro mb-3">// Contact</div>
          <h1 className="text-[#F7FAFC] text-4xl lg:text-5xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Speak to Peptigen Labs.
          </h1>
          <p className="text-[#9CA8B8] mt-4 leading-relaxed">
            For research enquiries, bulk lab supply, UK dispatch questions, or COA requests, send
            a message below.
          </p>

          <div className="mt-8 space-y-3">
            <Tile icon={Mail} label="info@peptigenlabs.co.uk" href="mailto:info@peptigenlabs.co.uk" testid="contact-tile-email" />
            <Tile icon={Building2} label="Peptigen Labs Ltd" sublabel="Company No. 17193157 · Registered in England & Wales" testid="contact-tile-company" />
            <Tile icon={Truck} label="Same-day UK dispatch · 14:00 cut-off" />
            <Tile icon={FileText} label="COA available on request — batch dependent" />
          </div>

          <div className="mt-10 p-5 border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04] rounded-sm">
            <div className="pg-micro text-[#A6FF00] mb-2">// Compliance Note</div>
            <p className="text-xs text-[#9CA8B8] leading-relaxed">
              We cannot provide advice relating to human use, dosage, administration,
              reconstitution, or therapeutic outcomes.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="pg-card p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name" required>
              <input
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="pg-input"
                data-testid="contact-name"
              />
            </Field>
            <Field label="Email" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="pg-input"
                data-testid="contact-email"
              />
            </Field>
            <Field label="Organisation">
              <input
                value={form.organisation}
                onChange={(e) => update("organisation", e.target.value)}
                className="pg-input"
                data-testid="contact-organisation"
              />
            </Field>
            <Field label="Enquiry Type">
              <select
                value={form.enquiry_type}
                onChange={(e) => update("enquiry_type", e.target.value)}
                className="pg-input"
                data-testid="contact-enquiry-type"
              >
                {enquiryTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Message" required>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="pg-input"
                  data-testid="contact-message"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                className="pg-btn-primary w-full sm:w-auto"
                data-testid="contact-submit"
              >
                <Send size={16} />
                {sending ? "Sending..." : "Send message"}
              </button>
            </div>
            {sent && (
              <div className="sm:col-span-2" data-testid="contact-sent-confirmation">
                <JunkFolderHint
                  email={sent}
                  what="our reply"
                  size="sm"
                />
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="pg-micro mb-2">
        {label} {required && <span className="text-[#A6FF00]">*</span>}
      </div>
      {children}
    </label>
  );
}

function Tile({ icon: Icon, label, sublabel, href, testid }) {
  const inner = (
    <>
      <Icon size={16} className="text-[#00C2D6] shrink-0" />
      <div className="flex flex-col">
        <span className="text-sm text-[#F7FAFC] leading-tight">{label}</span>
        {sublabel && (
          <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]/70 mt-1">
            {sublabel}
          </span>
        )}
      </div>
    </>
  );
  const cls = "flex items-center gap-3 p-4 border border-[#00C2D6]/20 rounded-sm transition-colors";
  if (href) {
    return (
      <a
        href={href}
        className={`${cls} hover:border-[#00C2D6]/50 hover:bg-[#00C2D6]/5`}
        data-testid={testid}
      >
        {inner}
      </a>
    );
  }
  return <div className={cls} data-testid={testid}>{inner}</div>;
}
